import type { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import type { RedoClient } from "@redotech/redo-api-client";
import { t, useTranslation } from "@redotech/redo-i18n";
import { createSetupIntent } from "@redotech/redo-merchant-general-app/client/stripe";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoClientContext } from "@redotech/redo-web/client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { StripeProvider } from "@redotech/stripe-react";
import { StripeElements } from "@redotech/stripe-react/elements";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeElements as StripeElementsType,
  Stripe as StripeJs,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js";
import {
  memo,
  useContext,
  useEffect,
  useState,
  type MutableRefObject,
} from "react";

type PaymentMethodType = "card" | "us_bank_account";

/** A non-default Stripe account the payment method is collected on. */
export interface BillingStripeAccountTarget {
  currency: Currency;
  publishableKey: string;
}

export type ConfirmSetupIntentResult =
  | {
      ok: true;
      paymentMethodID: string;
      /** Non-null only when a bank account still needs micro-deposit verification. */
      verificationClientSecret: string | null;
    }
  | { ok: false; message: string };

/**
 * Submits the mounted PaymentElement, then creates a SetupIntent and confirms
 * it against those details.
 * Shared by this file's form, the settings billing page, and the supplies
 * modal so all three confirm identically and report the same failures.
 */
export async function confirmSetupIntent({
  stripe,
  elements,
  redoClient,
  team,
  currency,
}: {
  stripe: StripeJs;
  elements: StripeElementsType;
  redoClient: RedoClient;
  team: { name: string; receipt_email: string };
  currency?: Currency;
}): Promise<ConfirmSetupIntentResult> {
  const submitError = await elements.submit();
  if (submitError?.error) {
    console.error(submitError.error);
    return {
      ok: false,
      message:
        submitError.error.message ??
        t("There was an issue confirming your payment method."),
    };
  }

  const setupIntent = await createSetupIntent(redoClient, { currency });
  if (!setupIntent?.client_secret) {
    return {
      ok: false,
      message: t(
        "We couldn't start a secure payment session. Please refresh the page and try again.",
      ),
    };
  }

  const confirmed = await stripe.confirmSetup({
    elements,
    clientSecret: setupIntent.client_secret,
    redirect: "if_required",
    confirmParams: {
      payment_method_data: {
        billing_details: {
          name: team.name,
          email: team.receipt_email.split(",")[0] || undefined,
        },
      },
      // None of our current payment methods require this, but it's still required.
      return_url: "https://app.getredo.com/settings/billing",
    },
  });

  if (confirmed.error) {
    console.error(confirmed.error);
    return {
      ok: false,
      message:
        confirmed.error.message ??
        t(
          "Your payment method couldn't be confirmed. Please check the details and try again.",
        ),
    };
  }

  const paymentMethod = confirmed.setupIntent?.payment_method;
  if (!paymentMethod) {
    return {
      ok: false,
      message: t(
        "Your payment method couldn't be confirmed. Please try again.",
      ),
    };
  }

  const result = confirmed.setupIntent;
  return {
    ok: true,
    paymentMethodID:
      typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id,
    verificationClientSecret:
      result.status === "requires_action" &&
      result.next_action?.type === "verify_with_microdeposits" &&
      typeof result.client_secret === "string"
        ? result.client_secret
        : null,
  };
}

/** Confirms the SetupIntent and persists it; resolves true on success. */
export type PaymentMethodSubmit = () => Promise<boolean>;

interface CollectPaymentMethodProps {
  team: { name: string; receipt_email: string };
  /** Confirms a SetupIntent; the caller persists it (e.g. updateStripe with
   * primary: true). secret is non-null only for microdeposit verifications. */
  onConfirmed: (
    paymentMethodID: string,
    setupIntentClientSecret: string | null,
  ) => void | Promise<void>;
  paymentMethodTypes?: PaymentMethodType[];
  submitLabel?: string;
  /** When set, the internal submit button is hidden and its submit function is
   * published here so an outside control (e.g. a footer button) can drive it. */
  submitRef?: MutableRefObject<PaymentMethodSubmit | null>;
  /** Collects a card on this account instead of the default one. */
  localizedAccount?: BillingStripeAccountTarget;
}

/**
 * Collects a Stripe payment method and hands the confirmed id to onConfirmed
 * so each caller controls where it's saved. Wraps its own StripeElements.
 */
export const CollectPaymentMethod = memo(function CollectPaymentMethod({
  team,
  onConfirmed,
  paymentMethodTypes = ["card", "us_bank_account"],
  submitLabel: submitLabelProp,
  submitRef,
  localizedAccount,
}: CollectPaymentMethodProps) {
  const { t } = useTranslation();
  const submitLabel = submitLabelProp ?? t("Save payment method");
  const elements = (
    <StripeElements
      options={{
        mode: "setup",
        currency: localizedAccount?.currency.toLowerCase() ?? "usd",
        setupFutureUsage: "off_session",
        payment_method_types: localizedAccount ? ["card"] : paymentMethodTypes,
      }}
    >
      <PaymentMethodForm
        currency={localizedAccount?.currency}
        onConfirmed={onConfirmed}
        submitLabel={submitLabel}
        submitRef={submitRef}
        team={team}
      />
    </StripeElements>
  );
  if (!localizedAccount) {
    return elements;
  }
  return (
    <StripeProvider publicKey={localizedAccount.publishableKey}>
      {elements}
    </StripeProvider>
  );
});

const PaymentMethodForm = memo(function PaymentMethodForm({
  team,
  onConfirmed,
  submitLabel,
  submitRef,
  currency,
}: {
  team: { name: string; receipt_email: string };
  onConfirmed: (
    paymentMethodID: string,
    setupIntentClientSecret: string | null,
  ) => void | Promise<void>;
  submitLabel: string;
  submitRef?: MutableRefObject<PaymentMethodSubmit | null>;
  currency?: Currency;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const redoClient = useRequiredContext(RedoClientContext);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);

  const handleChange = useHandler((event: StripePaymentElementChangeEvent) => {
    setComplete(!event.empty && event.complete);
  });

  const submit = useHandler(async (): Promise<boolean> => {
    if (!stripe || !elements) {
      return false;
    }
    const confirmed = await confirmSetupIntent({
      stripe,
      elements,
      redoClient,
      team,
      currency,
    });
    if (!confirmed.ok) {
      setError(confirmed.message);
      return false;
    }
    setError("");

    await onConfirmed(
      confirmed.paymentMethodID,
      confirmed.verificationClientSecret,
    );
    return true;
  });

  useEffect(() => {
    if (!submitRef) {
      return;
    }
    submitRef.current = submit;
    return () => {
      submitRef.current = null;
    };
  }, [submitRef, submit]);

  const [saveLoad, doSave] = useTriggerLoad(submit);

  return (
    <Flex dir="column" gap="md" w="full">
      <EditBillingForm error={error} handleChange={handleChange} />
      {!submitRef && (
        <RedoButton
          disabled={!complete}
          hierarchy="primary"
          onClick={() => doSave()}
          pending={saveLoad.pending}
          size="sm"
          text={submitLabel}
        />
      )}
    </Flex>
  );
});

/**
 * The Stripe PaymentElement plus inline error, shared by the settings billing
 * page and the onboarding billing step so both render the same card entry.
 */
export const EditBillingForm = ({
  handleChange,
  error,
}: {
  handleChange(event: StripePaymentElementChangeEvent): void;
  error: string;
}) => {
  const team = useContext(TeamContext);
  if (!team) {
    return null;
  }

  return (
    <Flex dir="column" gap="md" w="full">
      <PaymentElement
        onChange={handleChange}
        options={{
          fields: { billingDetails: { name: "never" } },
          defaultValues: {
            billingDetails: {
              email: team.receipt_email.split(",")[0] || undefined,
            },
          },
          paymentMethodOrder: ["us_bank_account", "card"],
        }}
      />
      {error && (
        <Text fontSize="sm" textColor="error">
          {error}
        </Text>
      )}
    </Flex>
  );
};
